document.documentElement.classList.add('celestial-js');

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!reduceMotion && 'IntersectionObserver' in window) {
  const revealObserver = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
  );

  document.querySelectorAll('.celestial-reveal').forEach((element) => revealObserver.observe(element));
} else {
  document.querySelectorAll('.celestial-reveal').forEach((element) => element.classList.add('is-visible'));
}

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('.celestial-reveal').forEach((element) => element.classList.add('is-visible'));
});

function setupMobileHeroProductOrbit(hero) {
  if (hero.dataset.productOrbitReady === 'true') return;
  hero.dataset.productOrbitReady = 'true';

  const items = [...hero.querySelectorAll(
    '.celestial-hero__orbit-item:nth-child(1), .celestial-hero__orbit-item:nth-child(2), .celestial-hero__orbit-item:nth-child(3), .celestial-hero__orbit-item:nth-child(5), .celestial-hero__orbit-item:nth-child(11)',
  )]
    .map((item) => ({ item, link: item.querySelector('.celestial-hero__product-link') }))
    .filter(({ link }) => link);
  let selectedItem = null;
  let resetTimer;
  let resetTransitionTimer;
  let selectedOrbitFrame;
  let suppressLinkClickUntil = 0;
  const orbitDuration = 26;
  const defaultAngles = [0, 72, 144, 216, 288];

  const stopSelectedOrbit = () => {
    window.cancelAnimationFrame(selectedOrbitFrame);
    selectedOrbitFrame = undefined;
  };

  const startSelectedOrbit = (remaining) => {
    const startedAt = performance.now();
    const renderOrbit = (now) => {
      const rotation = ((now - startedAt) / (orbitDuration * 1000)) * 360;
      remaining.forEach(({ item }, index) => {
        const angle = rotation + (index * 360) / remaining.length;
        item.style.transform = `translate(-50%, -50%) rotate(${angle}deg) translateY(-122px) rotate(${-angle}deg)`;
      });
      selectedOrbitFrame = window.requestAnimationFrame(renderOrbit);
    };
    selectedOrbitFrame = window.requestAnimationFrame(renderOrbit);
  };

  const resetOrbit = () => {
    window.clearTimeout(resetTimer);
    window.clearTimeout(resetTransitionTimer);
    stopSelectedOrbit();
    items.forEach(({ item, link }, index) => {
      const angle = defaultAngles[index];
      item.classList.remove('is-product-selected');
      item.style.transform = `translate(-50%, -50%) rotate(${angle}deg) translateY(-122px) rotate(${-angle}deg)`;
      item.style.animationDelay = `${-(angle / 360) * orbitDuration}s`;
      link.removeAttribute('aria-current');
    });
    selectedItem = null;

    resetTransitionTimer = window.setTimeout(() => {
      hero.classList.remove('is-product-selected');
      items.forEach(({ item }) => {
        item.style.removeProperty('transform');
        item.style.removeProperty('animation-delay');
      });
    }, 680);
  };

  const selectProduct = (selected) => {
    window.clearTimeout(resetTimer);
    window.clearTimeout(resetTransitionTimer);
    stopSelectedOrbit();
    hero.classList.add('is-product-selected');
    const remaining = items.filter(({ item }) => item !== selected.item);

    items.forEach(({ item, link }) => {
      const active = item === selected.item;
      const remainingIndex = remaining.findIndex((entry) => entry.item === item);
      item.classList.toggle('is-product-selected', active);
      link.toggleAttribute('aria-current', active);
      if (active) {
        item.style.transform = 'translate(-50%, -50%) scale(1.22)';
      } else {
        item.style.animationDelay = `${-(remainingIndex / remaining.length) * orbitDuration}s`;
      }
    });

    startSelectedOrbit(remaining);
    selectedItem = selected.item;
    resetTimer = window.setTimeout(resetOrbit, 3000);
  };

  // Intercept at pointer-down, before a mobile browser can follow the anchor.
  // A second tap is deliberately navigated by script so the link remains usable.
  hero.addEventListener('pointerdown', (event) => {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    if (event.button !== undefined && event.button !== 0) return;
    const link = event.target.closest('.celestial-hero__product-link');
    const selected = items.find((entry) => entry.link === link);
    if (!selected) {
      if (selectedItem && event.target.closest('.celestial-hero__product-orbit')) {
        resetOrbit();
      }
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (selectedItem === selected.item) {
      window.location.assign(link.href);
      return;
    }
    selectProduct(selected);
    suppressLinkClickUntil = Date.now() + 1200;
  }, true);

  // Some mobile browsers still emit a link click after pointerdown. This
  // capture-phase fallback prevents that synthetic first click from navigating.
  hero.addEventListener('click', (event) => {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    const link = event.target.closest('.celestial-hero__product-link');
    const selected = items.find((entry) => entry.link === link);
    if (!selected) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (Date.now() < suppressLinkClickUntil) return;
    if (selectedItem === selected.item) {
      window.location.assign(link.href);
      return;
    }
    selectProduct(selected);
    suppressLinkClickUntil = Date.now() + 1200;
  }, true);
}

document.querySelectorAll('.celestial-hero').forEach(setupMobileHeroProductOrbit);

function buildMobileGalleryDots(gallery) {
  if (gallery.dataset.mobileDotsReady === 'true') return;
  const controls = gallery.querySelector('.slider-buttons.quick-add-hidden');
  const galleryList = gallery.querySelector('[id^="Slider-Gallery"]');
  if (!controls || !galleryList) return;

  const slides = [...galleryList.children];
  if (slides.length < 2) return;
  controls.querySelector('.senlansp-gallery-dots')?.remove();

  const dots = document.createElement('div');
  dots.className = 'senlansp-gallery-dots';
  const dotButtons = slides.map((slide, index) => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'senlansp-gallery-dot';
    dot.setAttribute('aria-label', `View product image ${index + 1}`);
    dot.addEventListener('click', () => {
      galleryList.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    });
    dots.append(dot);
    return dot;
  });
  controls.append(dots);

  const updateDots = () => {
    const activeIndex = slides.reduce(
      (closestIndex, slide, index) =>
        Math.abs(slide.offsetLeft - galleryList.scrollLeft) < Math.abs(slides[closestIndex].offsetLeft - galleryList.scrollLeft)
          ? index
          : closestIndex,
      0,
    );
    dotButtons.forEach((dot, index) => dot.classList.toggle('is-active', index === activeIndex));
  };

  updateDots();
  gallery.dataset.mobileDotsReady = 'true';
  galleryList.addEventListener('scroll', () => window.requestAnimationFrame(updateDots), { passive: true });
}

function hideMobileProductGalleryVideos(scope = document) {
  if (!window.matchMedia('(max-width: 989px)').matches) return;

  scope.querySelectorAll('.template-product media-gallery').forEach((gallery) => {
    const galleryList = gallery.querySelector('[id^="Slider-Gallery"]');
    if (!galleryList) return;
    [...galleryList.children]
      .sort((first, second) => Number(first.dataset.mediaPosition) - Number(second.dataset.mediaPosition))
      .forEach((item) => galleryList.append(item));
    gallery.querySelectorAll('.product__media-item[data-media-type="image"]').forEach((item) => {
      item.style.setProperty('display', 'flex', 'important');
      item.style.setProperty('opacity', '1', 'important');
    });

    const videoItems = [...gallery.querySelectorAll('.product__media-item')].filter((item) =>
      item.querySelector('.media-type-video'),
    );
    const mediaIds = videoItems.map((item) => item.dataset.mediaId);
    videoItems.forEach((item) => item.remove());
    gallery.querySelectorAll('.thumbnail-list__item').forEach((thumbnail) => {
      if (mediaIds.includes(thumbnail.querySelector('[data-target]')?.dataset.target)) thumbnail.remove();
    });

    const total = gallery.querySelector('.slider-counter--total');
    if (total && galleryList) total.textContent = galleryList.children.length;
    const sliderComponent = galleryList.closest('slider-component');
    if (typeof sliderComponent?.resetPages === 'function') sliderComponent.resetPages();
    buildMobileGalleryDots(gallery);
  });
}

hideMobileProductGalleryVideos();

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('.celestial-hero').forEach(setupMobileHeroProductOrbit);
  hideMobileProductGalleryVideos(event.target);
});


